import {
  IFormFileDrawingItem,
  IProjectFileDrawingTempChunkCompleteRequest,
} from '@file/projectFile/projectFile.models';
import { IOption, Icon, UseFormReturn, removeItemInArray, useMsal } from '@gcl/react-core';
import { RowState } from '@gcl/react-core/dist/components/Table/UnstyledTableComponents/Layout/VerticalTableLayout';
import React, { Dispatch, MutableRefObject, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { IProjectTransmittalItem } from '@transmittal/transmittalItem/transmittalItem.models';
import {
  useCheckProjectTransmittalItemDocNo,
  useDeleteProjectTransmittalItemRevision,
  useDeleteTransmittalItem as useDeleteTransmittalItemMutation,
  useUpsertTransmittalItemsAndRevisions,
} from '@transmittal/transmittalItem/transmittalItem.queries';
import { useAppConfigs, useProjectId } from '@config/config.hooks';
import { keySeparator } from '@core/Primitives/text.services';
import { FETCH_QUERIES_NAME, KEY_SEPARATOR } from '@common/constants';
import { QueryKey, focusManager, useQueryClient } from '@tanstack/react-query';
import { ETransmittalStatus, ETransmittalType } from '@transmittal/transmittal.constants';
import { IProjectTransmittalItemDocumentType } from '@transmittal/transmittalItem/transmittalItemDocumentType/transmittalItemDocumentType.models';
import { AxiosErrorResponse } from '@core/error.models';
import {
  IProjectTransmittalItemRevision,
  IUpsertProjectTransmittalItemRevisionRequest,
} from '@transmittal/transmittalItemRevision/transmittalItemRevision.models';
import { getMasterScheduleDetail, useNominatedInMasterSchedule } from '@masterSchedule/masterSchedule.hooks';
import { useConfirmationModal } from '@common/confirmation.hooks';
import { v4 as uuidV4 } from 'uuid';
import { useFetchTransmittalById } from '@transmittal/transmittal.queries';
import { useUploadProjectFileDrawingTempAsync } from '@file/projectFile/projectFile.queries';
import { envConfigs } from '@config/envConfigs';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import {
  ITransmittalItemRowEditingEvents,
  ITransmittalItemRowEditingRef,
  ITransmittalItemTableEditingEvents,
  ITransmittalItemTableEditingRef,
  convertIssueDropdownOptions,
  transmittalItemRevisionSchema,
} from './transmittalItemTableForm.models';
import { ETransmittalDetailsFormKeys } from '../transmittalDetailPage.constants';
import {
  ETransmittalItemTableFormKey,
  TRANSMITTAL_ITEM_REVISION_NOT_ISSUE_KEY,
  transmittalItemRequestErrorKey,
} from './transmittalItemTableForm.constants';

export const getTransmittalGeneralDetail = (transmittalId: number) => {
  const projectId = useProjectId();
  const { data: transmittalDetails } = useFetchTransmittalById({ projectId }, transmittalId, false);

  const defaultFileType: EProjectFileType | undefined = React.useMemo(() => {
    switch (transmittalDetails?.type) {
      case ETransmittalType.DESIGNER:
        return EProjectFileType.DESIGNER_TRANSMITTAL_DRAWING;
      case ETransmittalType.SITE:
      case ETransmittalType.REVIEWER:
        return EProjectFileType.SITE_TRANSMITTAL_DRAWING;
      default:
        return undefined;
    }
  }, [transmittalDetails?.type]);

  return {
    transmittalStatus: transmittalDetails?.status,
    transmittalType: transmittalDetails?.type ?? ETransmittalType.DESIGNER,
    masterScheduleId: transmittalDetails?.masterScheduleId ?? 0,
    defaultFileType,
  };
};
export const getErrorKeyWithSeperator = (originalKey: string) => {
  const { key, id: uuid } = keySeparator(originalKey, KEY_SEPARATOR);
  const getKey = (keyLocal: string) => {
    switch (keyLocal?.toLocaleLowerCase()) {
      case transmittalItemRequestErrorKey.DOC_NO.toLocaleLowerCase():
        return ETransmittalItemTableFormKey.docNo;
      case transmittalItemRequestErrorKey.DESCRIPTION.toLocaleLowerCase():
        return ETransmittalItemTableFormKey.description;
      case transmittalItemRequestErrorKey.PROJECT_FILE.toLocaleLowerCase():
        return ETransmittalItemTableFormKey.projectFile;
      case transmittalItemRequestErrorKey.REVISION_NO.toLocaleLowerCase():
        return ETransmittalItemTableFormKey.revisionNo;
      default:
        return key;
    }
  };
  return { fieldKey: getKey(key), uuid };
};
export const validateFormValues = async (
  rowValues: IFormFileDrawingItem,
  useFormMethods: UseFormReturn,
  rowFormKeyPrefix: string,
) => {
  try {
    await transmittalItemRevisionSchema.validate(rowValues.form, { abortEarly: false });
    return true;
  } catch (error: any) {
    error?.errors?.forEach((err: { key: string; message: string }) => {
      useFormMethods.setError(`${rowFormKeyPrefix}.${ETransmittalItemTableFormKey.form}.${err.key}`, {
        message: err.message,
      });
    });
    return false;
  }
};
export const getTableDefaultValue = (useFormMethods: UseFormReturn, tableIndex: number) => {
  const originalFiles: IFormFileDrawingItem[] =
    ((useFormMethods.formState.defaultValues ?? {})[ETransmittalDetailsFormKeys.DOCUMENTS] ?? [])[tableIndex] ?? [];
  return originalFiles;
};

export const useFormPrefix = (tableIndex?: number, rowIndex?: number) => {
  const getTablePrefix = (tableIndexLocal: number | undefined) => {
    return tableIndexLocal !== undefined ? `${ETransmittalDetailsFormKeys.DOCUMENTS}[${tableIndexLocal}]` : '';
  };
  const getRowPrefix = (tableIndexLocal?: number, rowIndexLocal?: number) => {
    return tableIndexLocal !== undefined && rowIndexLocal !== undefined
      ? `${getTablePrefix(tableIndexLocal)}[${rowIndexLocal}]`
      : '';
  };
  const getRowFormPrefix = (tableIndexLocal?: number, rowIndexLocal?: number) => {
    return tableIndexLocal !== undefined && rowIndexLocal !== undefined
      ? `${getRowPrefix(tableIndexLocal, rowIndexLocal)}.${ETransmittalItemTableFormKey.form}`
      : '';
  };

  const tablePrefix = getTablePrefix(tableIndex);
  const rowPrefix = getRowPrefix(tableIndex, rowIndex);
  const rowFormPrefix = getRowFormPrefix(tableIndex, rowIndex);

  return { tablePrefix, rowPrefix, rowFormPrefix, getTablePrefix, getRowPrefix, getRowFormPrefix };
};

export const useSubmitFormEnabled = (useFormMethods: UseFormReturn, tableIndex: number | undefined) => {
  const { tablePrefix } = useFormPrefix(tableIndex);

  const allItems: IFormFileDrawingItem[][] | undefined = useFormMethods.watch(
    `${ETransmittalDetailsFormKeys.DOCUMENTS}`,
  );

  const isEnabled = useMemo(() => {
    let items: IFormFileDrawingItem[] = [];
    if (tableIndex && tablePrefix.length > 0) {
      items = (allItems ?? [])[tableIndex];
    } else if ((allItems ?? []).length > 0) {
      items = (allItems?.flat() ?? []).filter((s): s is IFormFileDrawingItem => !!s);
    }

    const temp = !items?.some(
      (s) =>
        s.form?.projectFile?.uploadState === 'error' ||
        s.form?.projectFile?.uploadState === 'uploadingItem' ||
        s.form?.projectFile?.uploadState === 'uploadingRevision',
    );

    return temp;
  }, [tableIndex, JSON.stringify(allItems), allItems]);

  return {
    isSubmitEnabled: isEnabled,
  };
};
export const getItemsOfUploadRevisionWithSameFileName = (
  useFormMethods: UseFormReturn,
  tableIndex?: number,
  rowIndex?: number,
) => {
  const allRows = useFormMethods.getValues(ETransmittalDetailsFormKeys.DOCUMENTS) as IFormFileDrawingItem[][];
  let rowsWithRevisionUpload: IFormFileDrawingItem[] = [];
  if (!tableIndex && !rowIndex) {
    rowsWithRevisionUpload = allRows.flat().filter((s): s is IFormFileDrawingItem => !!s);
  }
  if (tableIndex) {
    rowsWithRevisionUpload = allRows[tableIndex];
    if (rowIndex && rowsWithRevisionUpload[rowIndex]) {
      rowsWithRevisionUpload = [rowsWithRevisionUpload[rowIndex]];
    }
  }
  const newRevisionFilesWithSameFileName = rowsWithRevisionUpload.filter(
    (row) =>
      row.form?.projectFile &&
      row.form?.projectFile?.fileName ===
        (row.transmittalItem?.transmittalRevision?.projectFile?.fileName ??
          row.transmittalItem?.latestRevision?.projectFile?.fileName),
  );
  return newRevisionFilesWithSameFileName;
};

export const useSubmitRow = (
  masterScheduleId: number,
  transmittalId: number,
  transmittalType: ETransmittalType,
  useFormMethods: UseFormReturn,
) => {
  const projectId = useProjectId();

  const { update: updateCacheFromUpdateTransmittalItemResponseByDocId } =
    useUpdateCacheFromUpdateTransmittalItemResponseByDocId(masterScheduleId, transmittalId);
  const { getRowFormPrefix, getRowPrefix } = useFormPrefix();

  const [validationError, setValidationError] = useState<string | null>(null);

  const mutationUpsertTransmittalItemsAndRevisions = useUpsertTransmittalItemsAndRevisions();
  const {
    mutate: mutateUpsertTransmittalItemsAndRevisions,
    mutateAsync: mutateUpsertTransmittalItemsAndRevisionsAsync,
    isLoading: isLoadingUpsertTransmittalItemsAndRevisions,
    isError: isErrorUpsertTransmittalItemsAndRevisions,
    isSuccess: isSuccessUpsertTransmittalItemsAndRevisions,
    error: errorUpsertTransmittalItemsAndRevisions,
    reset: resetUpsertTransmittalItemsAndRevisions,
    data: dataUpsertTransmittalItemsAndRevisions,
  } = mutationUpsertTransmittalItemsAndRevisions;

  const {
    mutateAsync: mutateCheckProjectTransmittalItemDocNoAsync,
    isLoading: isLoadingCheckProjectTransmittalItemDocNo,
    error: errorCheckProjectTransmittalItemDocNo,
    isError: isErrorCheckProjectTransmittalItemDocNo,
    isSuccess: isSuccessCheckProjectTransmittalItemDocNo,
    reset: resetCheckProjectTransmittalItemDocNo,
  } = useCheckProjectTransmittalItemDocNo();

  const mutationUpsertTransmittalItemsAndRevisionsForTables = {
    isLoading: isLoadingCheckProjectTransmittalItemDocNo || isLoadingUpsertTransmittalItemsAndRevisions,
    isError: isErrorCheckProjectTransmittalItemDocNo || isErrorUpsertTransmittalItemsAndRevisions,
    error: errorCheckProjectTransmittalItemDocNo || errorUpsertTransmittalItemsAndRevisions || validationError,
    isSuccess: isSuccessCheckProjectTransmittalItemDocNo && isSuccessUpsertTransmittalItemsAndRevisions,
    data: dataUpsertTransmittalItemsAndRevisions,
    reset: () => {
      resetCheckProjectTransmittalItemDocNo();
      resetUpsertTransmittalItemsAndRevisions();
      setValidationError(null);
    },
  };

  const [uuidMap, setUuidMap] = useState<Map<string, string>>(new Map());

  const errorKeySeperator = useCallback(
    (originalKey: string) => {
      const { fieldKey, uuid } = getErrorKeyWithSeperator(originalKey);
      return `${uuidMap.get(uuid)}.${fieldKey}`;
    },
    [uuidMap],
  );

  const checkAnyUnfinishedRequestInMobile = () => {
    const tables = (useFormMethods.getValues(ETransmittalDetailsFormKeys.DOCUMENTS) as IFormFileDrawingItem[][]) ?? [];
    const isAnyUnfinished = tables.some((table) => {
      return table.some((row) => {
        return !row.transmittalItem || row.form?.projectFile;
      });
    });
    return isAnyUnfinished;
  };

  const onClickSubmitSingleRow = async (
    tableIndex: number,
    rowIndex: number,
    setRowState: Dispatch<SetStateAction<RowState>> | undefined,
  ) => {
    const formKeyPrefix = `${ETransmittalDetailsFormKeys.DOCUMENTS}[${tableIndex}]`;

    let itemsToProcess: {
      [key: string]: {
        rowValues: IFormFileDrawingItem;
      };
    } = {};

    const rowValues = (useFormMethods.getValues(getRowPrefix(tableIndex, rowIndex)) ?? {}) as IFormFileDrawingItem;

    itemsToProcess = {
      ...itemsToProcess,
      [`${formKeyPrefix}[${rowIndex}]`]: { rowValues },
    };
    if (setRowState) setRowState('Loading');
    if (!rowValues.form) {
      if (setRowState) setRowState('Editing');
      return;
    }

    useFormMethods.clearErrors();

    const result = await onHandleSubmitMultiRows(itemsToProcess);
  };

  const onClickSubmitTable = async (
    tableIndex: number,
    refRowEditingEvents: MutableRefObject<ITransmittalItemRowEditingRef>,
  ) => {
    setUuidMap(
      new Map(
        Object.keys(refRowEditingEvents.current).map((rowIndexStr) => {
          const rowIndex = parseInt(rowIndexStr, 10);
          return [
            useFormMethods.getValues(
              `${getRowFormPrefix(tableIndex, rowIndex)}.${ETransmittalItemTableFormKey.uuid}`,
            ) as string,
            `${getRowFormPrefix(tableIndex, rowIndex)}`,
          ];
        }),
      ),
    );

    const formKeyPrefix = `${ETransmittalDetailsFormKeys.DOCUMENTS}[${tableIndex}]`;

    useFormMethods.clearErrors(`${formKeyPrefix}`);

    let itemsToProcess: {
      [key: string]: {
        rowValues: IFormFileDrawingItem;
      };
    } = {};
    Object.entries(refRowEditingEvents.current).map((row) => {
      const rowIndex = parseInt(row[0], 10);
      const rowValues = (useFormMethods.getValues(`${formKeyPrefix}[${rowIndex}]`) ?? {}) as IFormFileDrawingItem;

      itemsToProcess = {
        ...itemsToProcess,
        [`${formKeyPrefix}[${rowIndex}]`]: { rowValues },
      };
    });

    const result = await onHandleSubmitMultiRows(itemsToProcess);
    return result;
  };

  const onClickSubmitForm = async (
    transmittalItemTableEditingRef: MutableRefObject<ITransmittalItemTableEditingRef>,
  ) => {
    setUuidMap(
      new Map(
        Object.entries(transmittalItemTableEditingRef.current).flatMap(([tableIndexStr, tableEvents]) => {
          const tableIndex = parseInt(tableIndexStr, 10);
          const { tableRef } = tableEvents;
          return Object.keys((tableRef as MutableRefObject<ITransmittalItemRowEditingRef>).current).map(
            (rowIndexStr) => {
              const rowIndex = parseInt(rowIndexStr, 10);
              return [
                useFormMethods.getValues(
                  `${getRowFormPrefix(tableIndex, rowIndex)}.${ETransmittalItemTableFormKey.uuid}`,
                ) as string,
                `${getRowFormPrefix(tableIndex, rowIndex)}`,
              ];
            },
          );
        }),
      ),
    );

    let itemsToProcess: {
      [key: string]: {
        rowValues: IFormFileDrawingItem;
      };
    } = {};

    Object.entries(transmittalItemTableEditingRef.current).map((table) => {
      const tableIndex = parseInt(table[0], 10);
      const { tableRef } = table[1] as ITransmittalItemTableEditingEvents;

      const formKeyPrefix = `${ETransmittalDetailsFormKeys.DOCUMENTS}[${tableIndex}]`;
      Object.entries(tableRef?.current).map((row) => {
        const rowEvent = row[1] as ITransmittalItemRowEditingEvents;
        if (rowEvent.setRowState) rowEvent.setRowState('Loading');

        const rowIndex = parseInt(row[0], 10);
        const rowValues = (useFormMethods.getValues(`${formKeyPrefix}[${rowIndex}]`) ?? {}) as IFormFileDrawingItem;

        itemsToProcess = {
          ...itemsToProcess,
          [`${formKeyPrefix}[${rowIndex}]`]: { rowValues },
        };
      });
    });

    useFormMethods.clearErrors();

    const result = await onHandleSubmitMultiRows(itemsToProcess);
    return result;
  };

  const onHandleSubmitMultiRows = async (itemsToProcess: { [key: string]: { rowValues: IFormFileDrawingItem } }) => {
    // validate all rows
    // check doc nos
    // submit each row

    const isValid = await Promise.all(
      Object.entries(itemsToProcess).map(async (row) => {
        const rowFormKeyPrefix: string = row[0];
        const rowValues = row[1].rowValues as IFormFileDrawingItem;

        const isValidRow = await validateFormValues(rowValues, useFormMethods, rowFormKeyPrefix);
        return isValidRow;
      }),
    );
    if (isValid.some((s) => s === false)) {
      setValidationError('Validation Error');
      return undefined;
    }

    const docNos = Object.entries(itemsToProcess).map((row) => {
      const rowValues = row[1].rowValues as IFormFileDrawingItem;

      return {
        docNo: rowValues.form?.item?.docNo,
        uuid: rowValues.form?.uuid ?? '',
        projectId,
        itemId: rowValues.form?.item?.id,
      };
    });

    try {
      const result = await mutateCheckProjectTransmittalItemDocNoAsync(docNos);
      if (!result) return undefined;
    } catch (err) {
      const errorCheckProjectTransmittalItemDocNoLocal = err as AxiosErrorResponse;
      const errors = Object.keys(errorCheckProjectTransmittalItemDocNoLocal?.response?.data.errors ?? {});
      errors.map((error) => {
        const found = Object.entries(itemsToProcess).find((row) => {
          const rowUuid = (row[1].rowValues as IFormFileDrawingItem).form?.uuid ?? '';
          return error.startsWith(rowUuid);
        });
        if (found)
          useFormMethods.setError(
            `${found[0]}.${ETransmittalItemTableFormKey.form}.${ETransmittalItemTableFormKey.docNo}`,
            {
              message: 'Duplicated doc no.',
            },
          );
      });
      return undefined;
    }

    try {
      const request = Object.values(itemsToProcess).map((s) => convertRequest(s.rowValues));
      const result = await mutateUpsertTransmittalItemsAndRevisionsAsync({ request, transmittalId });

      return true;
    } catch (error) {
      return false;
    }
  };

  const convertRequest = (rowValues: IFormFileDrawingItem) => {
    const req = {
      ...rowValues.form,
      action: rowValues.form?.projectFile ? 'upload' : 'updateItem',
      projectFile: rowValues.form?.projectFile
        ? {
            id: rowValues.transmittalItem?.transmittalRevision?.projectFile?.id,
            projectId: rowValues.form?.projectFile?.projectId ?? projectId,
            uuid: rowValues.form?.projectFile?.uuid,
            fileName: rowValues.form?.projectFile?.fileName,
            fileType: rowValues.form?.projectFile?.fileType,
            url: rowValues.form?.projectFile?.url,
            rowVersion: rowValues.transmittalItem?.transmittalRevision?.projectFile?.rowVersion,
          }
        : undefined,
    } as IUpsertProjectTransmittalItemRevisionRequest;
    return req;
  };

  const updateCacheFromUpdateTransmittalItemResponse = (
    dataUpdatedTransmittalItems: IProjectTransmittalItem[] | undefined,
  ) => {
    if (!dataUpdatedTransmittalItems || dataUpdatedTransmittalItems.length <= 0) return;

    let groupByDocTypeId: { [key: number]: IProjectTransmittalItem[] } = {};
    dataUpdatedTransmittalItems.map((dataUpdatedTransmittalItem) => {
      const docTypeId = dataUpdatedTransmittalItem.docType ?? 0;
      groupByDocTypeId = {
        ...groupByDocTypeId,
        [docTypeId]: [...(groupByDocTypeId[docTypeId] ?? []), dataUpdatedTransmittalItem],
      };
    });

    Object.entries(groupByDocTypeId).map((group) => {
      const docTypeId = parseInt(group[0], 10);
      updateCacheFromUpdateTransmittalItemResponseByDocId(docTypeId, group[1] ?? []);
    });
  };

  return {
    onClickSubmitSingleRow,
    onClickSubmitTable,
    onClickSubmitForm,
    updateCacheFromUpdateTransmittalItemResponse,
    mutationUpsertTransmittalItemsAndRevisions,
    mutationUpsertTransmittalItemsAndRevisionsForTables,
    errorKeySeperator,
    checkAnyUnfinishedRequestInMobile,
  };
};

export const useUploadProjectFileDrawingTemp = (
  item: IFormFileDrawingItem,
  uuid: string | undefined,
  tableIndex: number,
  rowIndex: number,
  useFormMethods: UseFormReturn,
  setFilesToUpload: React.Dispatch<
    React.SetStateAction<{
      [key: string]: File;
    }>
  >,
  setIsBlockRefetch: (value: React.SetStateAction<boolean>) => void,
  setRowState: React.Dispatch<React.SetStateAction<RowState>> | undefined,
) => {
  const { tablePrefix, rowPrefix, rowFormPrefix } = useFormPrefix(tableIndex, rowIndex);
  const appConfigs = useAppConfigs();
  const [projectFile, setProjectFile] = React.useState<IProjectFileDrawingTempChunkCompleteRequest>();
  const [uploadProgress, setUploadProgress] = React.useState<number | undefined>(undefined);

  const { instance } = useMsal();
  const mutationUploadProjectFileDrawingTemp = useUploadProjectFileDrawingTempAsync({
    apiTokenProps: {
      scopes: [envConfigs.apiScope as string],
      instance,
    },
    traceId: appConfigs.configs.traceId,
  });
  const {
    data: dataUploadProjectFileDrawingTemp,
    error: errorUploadProjectFileDrawingTemp,
    reset: resetUploadProjectFileDrawingTemp,
    isSuccess: isSuccessUploadProjectFileDrawingTemp,
  } = mutationUploadProjectFileDrawingTemp;

  React.useEffect(() => {
    if (isSuccessUploadProjectFileDrawingTemp) {
      let row = useFormMethods.getValues(rowPrefix);
      row = {
        ...row,
        form: {
          ...row.form,
          revisionNo:
            useFormMethods.getValues(`${rowFormPrefix}.${ETransmittalItemTableFormKey.projectFileUploadState}`) ===
            'uploadingRevision'
              ? null
              : row.form.revisionNo,
          lastModifiedDateTime: Date.now(),
          projectFile: {
            ...row.form?.projectFile,
            url: dataUploadProjectFileDrawingTemp,
            uploadState: 'finish',
          },
        },
      };
      setFilesToUpload((old) => {
        const newFiles = { ...old };
        if (uuid) delete newFiles[uuid];
        return newFiles;
      });
      const newDocs = [...useFormMethods.getValues(ETransmittalDetailsFormKeys.DOCUMENTS)] as IFormFileDrawingItem[][];
      newDocs[tableIndex][rowIndex] = row;
      useFormMethods.setValue(ETransmittalDetailsFormKeys.DOCUMENTS, newDocs);

      setRowState?.('Editing');
      setIsBlockRefetch(false);
      setUploadProgress(undefined);
      setProjectFile(undefined);
      resetUploadProjectFileDrawingTemp();
    }
  }, [JSON.stringify(dataUploadProjectFileDrawingTemp)]);

  React.useEffect(() => {
    if (!errorUploadProjectFileDrawingTemp) return;

    setFilesToUpload((old) => {
      const newFiles = { ...old };
      if (uuid) delete newFiles[uuid];
      return newFiles;
    });
    useFormMethods.setValue(`${rowFormPrefix}.${ETransmittalItemTableFormKey.projectFileUploadState}`, 'error');
    useFormMethods.setValue(`${tablePrefix}`, useFormMethods.getValues(`${tablePrefix}`));

    setRowState?.('Idle');
  }, [JSON.stringify(errorUploadProjectFileDrawingTemp)]);

  const { renderModal: renderErrorConfirmationPortal, openPortal: openErrorConfirmationPortal } = useConfirmationModal({
    isShowCancelBtn: false,
    content: (
      <>
        {errorUploadProjectFileDrawingTemp?.response?.data.message ??
          errorUploadProjectFileDrawingTemp?.response?.data.messages?.join('\n') ??
          `Upload ${projectFile?.fileName || ''} failed. Please reupload.`}
      </>
    ),
    onConfirm: () => {
      if (!item?.transmittalItem) {
        useFormMethods.setValue(
          tablePrefix,
          removeItemInArray(useFormMethods.getValues(tablePrefix) as IFormFileDrawingItem[], rowIndex),
        );
        return;
      }
      resetUploadProjectFileDrawingTemp();
      setProjectFile(undefined);

      useFormMethods.resetField(`${rowFormPrefix}.${ETransmittalItemTableFormKey.uuid}`);
      useFormMethods.resetField(`${rowFormPrefix}.${ETransmittalItemTableFormKey.projectFile}`);
      setFilesToUpload((old) => {
        const newFiles = { ...old };
        if (uuid) delete newFiles[uuid];
        return newFiles;
      });
      useFormMethods.setValue(`${tablePrefix}`, useFormMethods.getValues(`${tablePrefix}`));
      setUploadProgress(undefined);
    },
  });

  return {
    mutationUploadProjectFileDrawingTemp,
    projectFile,
    setProjectFile,
    uploadProgress,
    setUploadProgress,
    renderErrorConfirmationPortal,
    openErrorConfirmationPortal,
  };
};

export const useDeleteTransmittalItem = (
  transmittalId: number,
  item: IFormFileDrawingItem | undefined,
  setRowState: Dispatch<SetStateAction<RowState>> | undefined,
) => {
  const mutation = useDeleteTransmittalItemMutation();
  const { mutate: mutateDeleteTransmittalItem } = mutation;

  const { renderModal: renderDeleteTransmittalItemModal, openPortal: openDeleteTransmittalItemModal } =
    useConfirmationModal({
      content: 'This transmittal item and its revision will be deleted. Do you wish to continue?',
      onConfirm: () => {
        if (item?.form?.item?.id) {
          if (setRowState) setRowState('Loading');
          mutateDeleteTransmittalItem({ transmittalId, transmittalItemId: item.form?.item?.id });
        }
      },
    });

  return {
    useDeleteTransmittalItemMutation: mutation,
    renderDeleteTransmittalItemModal,
    openDeleteTransmittalItemModal,
  };
};

export const useDeleteTransmittalItemRevision = (
  transmittalId: number,
  prevRevision: string | undefined,
  downloadFileText: string | undefined,
  itemRevisionId: number | undefined,
  rowIndex: number,
  setRowUploading: ((index: number, isUploading: boolean) => void) | undefined,
) => {
  const projectId = useProjectId();

  const { renderModal: renderDeleteRevisionModal, openPortal: openDeleteRevisionModal } = useConfirmationModal({
    content: (
      <>
        Only unapproved revision file will be removed.
        <br />
        Transmittal revision will be rolled back from &quot;<b>{downloadFileText}</b>&quot; to{' '}
        <Icon iconName="DashCircle" className="gcl-gems-transmittal-item-download-column-not-issued-icon" />{' '}
        {prevRevision ? (
          <>
            (<b>{prevRevision}</b>)
          </>
        ) : (
          ''
        )}
        <br />
        Do you wish to continue?
      </>
    ),
    onConfirm: () => {
      if (!itemRevisionId) return;
      if (setRowUploading) setRowUploading(rowIndex, true);
      mutateDeleteProjectTransmittalItemRevision({ projectId, itemRevisionId: itemRevisionId ?? 0, transmittalId });
    },
  });

  const useDeleteProjectTransmittalItemRevisionMutation = useDeleteProjectTransmittalItemRevision();
  const {
    mutate: mutateDeleteProjectTransmittalItemRevision,
    isSuccess: isSuccessDeleteProjectTransmittalItemRevision,
    isLoading: isLoadingDeleteProjectTransmittalItemRevision,
    isError: isErrorDeleteProjectTransmittalItemRevision,
    reset: resetDeleteProjectTransmittalItemRevision,
    error: errorDeleteProjectTransmittalItemRevision,
  } = useDeleteProjectTransmittalItemRevisionMutation;
  return { useDeleteProjectTransmittalItemRevisionMutation, renderDeleteRevisionModal, openDeleteRevisionModal };
};

export const useUpdateCacheFromUpdateTransmittalItemResponseByDocId = (
  masterScheduleId: number,
  transmittalId: number,
) => {
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  const update = (docTypeId: number, transmittalItemsToUpdate: IProjectTransmittalItem[]) => {
    const queryKey: QueryKey = [
      projectId,
      masterScheduleId,
      FETCH_QUERIES_NAME.TRANSMITTAL_ITEMS,
      transmittalId,
      docTypeId,
    ];

    const prevDataList: IProjectTransmittalItem[] = queryClient.getQueryData(queryKey) ?? [];
    const merge: IProjectTransmittalItem[] = [...prevDataList, ...transmittalItemsToUpdate];
    let newDataList = [
      ...new Map(
        merge.map((s) => {
          return [s.id, s];
        }),
      ).values(),
    ];
    newDataList = sortCacheByDocNo(newDataList);
    queryClient.setQueryData(queryKey, newDataList);
  };

  return { update };
};
const sortCacheByDocNo = (list: IProjectTransmittalItem[]) => {
  return list.sort((a, b) => {
    const aString = a.docNo ?? '';
    const bString = b.docNo ?? '';
    return aString.localeCompare(bString);
  });
};

export const useSiteTransmittalIssueRevision = (
  masterScheduleId: number,
  transmittalId: number,
  rowIndex: number,
  item: IFormFileDrawingItem | undefined,
  docType: IProjectTransmittalItemDocumentType,
  setRowUploading: ((index: number, isUploading: boolean) => void) | undefined,
) => {
  const projectId = useProjectId();
  const dropdownOptions = useMemo(() => {
    return convertIssueDropdownOptions(item?.transmittalItem?.issuableRevisions ?? []);
  }, [JSON.stringify(item?.transmittalItem?.issuableRevisions)]);

  const [localValue, setLocalValue] = React.useState<
    IOption<IProjectTransmittalItemRevision | undefined> | undefined
  >();

  const latestRevisionId = useMemo(() => {
    const ids = item?.transmittalItem?.issuableRevisions?.map((r) => r.id).filter((r): r is number => r !== null) ?? [];
    return Math.max(...ids);
  }, [JSON.stringify(item?.transmittalItem?.issuableRevisions)]);

  const isLatestRevision = useMemo(() => {
    return (
      (item?.transmittalItem?.issuableRevisions &&
        localValue?.data?.id ===
          item.transmittalItem?.issuableRevisions[(item.transmittalItem?.issuableRevisions?.length ?? 0) - 1]?.id) ??
      false
    );
  }, [JSON.stringify(item?.transmittalItem?.issuableRevisions), localValue]);

  const optionFromItem = useMemo(() => {
    const found = dropdownOptions.find(
      (option) => option.data?.revisionNo === item?.transmittalItem?.transmittalRevision?.revisionNo,
    );
    return found ?? dropdownOptions[dropdownOptions.length - 1];
  }, [JSON.stringify(item?.transmittalItem?.transmittalRevision), JSON.stringify(dropdownOptions)]);

  useEffect(() => {
    setLocalValue(optionFromItem);
  }, [JSON.stringify(item?.transmittalItem?.transmittalRevision)]);

  const mutationUpsertTransmittalItemsAndRevisions = useUpsertTransmittalItemsAndRevisions();
  const {
    mutate: mutateUpsertTransmittalItemsAndRevisions,
    mutateAsync: mutateUpsertTransmittalItemsAndRevisionsAsync,
    isLoading: isLoadingUpsertTransmittalItemsAndRevisions,
    isError: isErrorUpsertTransmittalItemsAndRevisions,
    isSuccess: isSuccessUpsertTransmittalItemsAndRevisions,
    error: errorUpsertTransmittalItemsAndRevisions,
    reset: resetUpsertTransmittalItemsAndRevisions,
    data: dataUpsertTransmittalItemsAndRevisions,
  } = mutationUpsertTransmittalItemsAndRevisions;

  const { update: updateCacheFromUpdateTransmittalItemResponseByDocId } =
    useUpdateCacheFromUpdateTransmittalItemResponseByDocId(masterScheduleId ?? 0, transmittalId);

  useEffect(() => {
    if (!isSuccessUpsertTransmittalItemsAndRevisions) return;
    if (setRowUploading) setRowUploading(rowIndex, false);

    updateCacheFromUpdateTransmittalItemResponseByDocId(docType.id, dataUpsertTransmittalItemsAndRevisions.items ?? []);
    resetUpsertTransmittalItemsAndRevisions();
  }, [isSuccessUpsertTransmittalItemsAndRevisions]);

  const onMutate = useCallback(
    (newValue: IOption<IProjectTransmittalItemRevision | undefined>) => {
      if (newValue?.key === TRANSMITTAL_ITEM_REVISION_NOT_ISSUE_KEY) {
        if (!item?.form) return;
        if (setRowUploading) setRowUploading(rowIndex, true);

        mutateUpsertTransmittalItemsAndRevisions({ request: [{ ...item.form, action: 'delete' }], transmittalId });
        return;
      }
      if (!newValue.data) return;

      if (item?.form) {
        mutateUpsertTransmittalItemsAndRevisions({
          request: [
            {
              ...item.form,
              action: 'issue',
              issueFromProjectFileId: newValue.data.projectFile?.id,
              revisionNo: newValue.data.revisionNo,
            },
          ],
          transmittalId,
        });
        return;
      }
      mutateUpsertTransmittalItemsAndRevisions({
        request: [
          {
            uuid: uuidV4(),
            masterScheduleId,
            projectId,
            transmittalId,
            item: {
              projectId,
              masterScheduleId,
              docNo: item?.transmittalItem?.docNo,
              description: item?.transmittalItem?.description,
              id: item?.transmittalItem?.id,
              docType: item?.transmittalItem?.docType,
              inUse: item?.transmittalItem?.inUse,
              siteInUse: item?.transmittalItem?.siteInUse,
              isRegistered: item?.transmittalItem?.isRegistered,
              type: item?.transmittalItem?.type,
              lastTransmittalId: item?.transmittalItem?.lastTransmittalId,
              lastSiteTransmittalId: item?.transmittalItem?.lastSiteTransmittalId,
              rowVersion: item?.transmittalItem?.rowVersion,
            },
            revisionNo: newValue.data.revisionNo,
            action: 'issue',
            issueFromProjectFileId: newValue.data.projectFile?.id,
          },
        ],
        transmittalId,
      });
    },
    [JSON.stringify(item?.form)],
  );

  return {
    dropdownOptions,
    onMutate,
    isLatestRevision,
    latestRevisionId,
    mutationUpsertTransmittalItemsAndRevisions,
    setLocalValue,
    localValue,
    optionFromItem,
  };
};

export const useSiteTransmittalIssuableRevision = () => {
  const getLatestRevisionId = (item: IFormFileDrawingItem) => {
    const ids = item.transmittalItem?.issuableRevisions?.map((r) => r.id).filter((r): r is number => r !== null) ?? [];
    const latestRevision = Math.max(...ids);
    return latestRevision;
  };
  const isLatestRevision = (item: IFormFileDrawingItem) => {
    if (!item.transmittalItem?.inUse) return true;
    if (!item.transmittalItem?.issuableRevisions || item.transmittalItem?.issuableRevisions.length <= 0) return true;
    const latestRevisionId = getLatestRevisionId(item);
    const latestRevision = item.transmittalItem.issuableRevisions.find((s) => s.id === latestRevisionId);
    return (
      item.transmittalItem?.issuableRevisions &&
      item.transmittalItem?.transmittalRevision?.revisionNo === latestRevision?.revisionNo
    );
  };

  return { getLatestRevisionId, isLatestRevision };
};

export const useSiteTransmittalIssuableRevisionUpdateAll = (
  masterScheduleId: number,
  transmittalId: number,
  docTypeId: number,
  items: IFormFileDrawingItem[],
) => {
  const { isLatestRevision } = useSiteTransmittalIssuableRevision();

  const { update: updateCacheFromUpdateTransmittalItemResponseByDocId } =
    useUpdateCacheFromUpdateTransmittalItemResponseByDocId(masterScheduleId, transmittalId);

  const mutationUpsertTransmittalItemsAndRevisions = useUpsertTransmittalItemsAndRevisions();
  const {
    mutate: mutateUpsertTransmittalItemsAndRevisions,
    mutateAsync: mutateUpsertTransmittalItemsAndRevisionsAsync,
    isLoading: isLoadingUpsertTransmittalItemsAndRevisions,
    isError: isErrorUpsertTransmittalItemsAndRevisions,
    isSuccess: isSuccessUpsertTransmittalItemsAndRevisions,
    error: errorUpsertTransmittalItemsAndRevisions,
    reset: resetUpsertTransmittalItemsAndRevisions,
    data: dataUpsertTransmittalItemsAndRevisions,
  } = mutationUpsertTransmittalItemsAndRevisions;

  const isAllLatestRevision = useMemo(() => {
    const allIsLatestRevision = (items ?? []).map((item) => isLatestRevision(item));
    return allIsLatestRevision.every((s) => s === true);
  }, [JSON.stringify(items)]);

  useEffect(() => {
    if (!dataUpsertTransmittalItemsAndRevisions) return;
    updateCacheFromUpdateTransmittalItemResponseByDocId(docTypeId, dataUpsertTransmittalItemsAndRevisions?.items ?? []);
    resetUpsertTransmittalItemsAndRevisions();
  }, [JSON.stringify(dataUpsertTransmittalItemsAndRevisions)]);

  const updateAllToLatestRevision = useCallback(() => {
    const requests = items
      .filter((s) => {
        return !isLatestRevision(s);
      })
      .map((s) => {
        if (!s.form) return undefined;
        const latestRevision =
          s.transmittalItem?.issuableRevisions?.[(s.transmittalItem.issuableRevisions?.length ?? 0) - 1];
        const request: IUpsertProjectTransmittalItemRevisionRequest = {
          ...s.form,
          revisionNo: latestRevision?.revisionNo,
          action: 'issue',
          issueFromProjectFileId: latestRevision?.projectFile?.id,
        };
        return request;
      });

    mutateUpsertTransmittalItemsAndRevisions({
      transmittalId,
      request: requests.filter((s): s is IUpsertProjectTransmittalItemRevisionRequest => !!s),
    });
  }, [JSON.stringify(items)]);

  return {
    isAllLatestRevision,
    updateAllToLatestRevision,
    mutationUpdateAllToLatestRevision: mutationUpsertTransmittalItemsAndRevisions,
  };
};

export const useCheckerTransmittalRevisionsForApproval = (
  masterScheduleId: number,
  transmittalId: number,
  items: IFormFileDrawingItem[],
  docType: IProjectTransmittalItemDocumentType,
) => {
  const projectId = useProjectId();
  const { masterSchedule } = getMasterScheduleDetail(projectId, masterScheduleId);
  const { transmittalStatus, transmittalType } = getTransmittalGeneralDetail(transmittalId);
  const { isNominatedChecker: isPermittedSendApproval } = useNominatedInMasterSchedule(masterSchedule);

  const { update: updateCacheFromUpdateTransmittalItemResponseByDocId } =
    useUpdateCacheFromUpdateTransmittalItemResponseByDocId(masterScheduleId, transmittalId);

  const mutationUpsertTransmittalItemsAndRevisions = useUpsertTransmittalItemsAndRevisions();
  const {
    mutate: mutateUpsertTransmittalItemsAndRevisions,
    mutateAsync: mutateUpsertTransmittalItemsAndRevisionsAsync,
    isLoading: isLoadingUpsertTransmittalItemsAndRevisions,
    isError: isErrorUpsertTransmittalItemsAndRevisions,
    isSuccess: isSuccessUpsertTransmittalItemsAndRevisions,
    error: errorUpsertTransmittalItemsAndRevisions,
    reset: resetUpsertTransmittalItemsAndRevisions,
    data: dataUpsertTransmittalItemsAndRevisions,
  } = mutationUpsertTransmittalItemsAndRevisions;

  const isSelectAllTransmittalRevisionsForApproval = useMemo(() => {
    const countForInUseItems = items?.filter((s) => s.transmittalItem?.inUse).length ?? 0;
    const isShow = countForInUseItems > 0 && docType.allowUpload && isPermittedSendApproval;

    const countForSelectedApprovalItems = isShow
      ? items?.filter((s) => s.transmittalItem?.inUse && s.transmittalItem.transmittalRevision?.isIceDesignCertified)
          .length ?? 0
      : 0;

    return {
      isShow: transmittalStatus === ETransmittalStatus.DRAFT && transmittalType === ETransmittalType.ICE && isShow,
      isSelectAll: countForSelectedApprovalItems < countForInUseItems,
    };
  }, [JSON.stringify(items), docType, isPermittedSendApproval]);

  const onClickSelectAll = useCallback(
    (isSelectAll: boolean) => {
      const request: IUpsertProjectTransmittalItemRevisionRequest[] = items
        .filter((s) => {
          return s.form?.isIceDesignCertified === !isSelectAll;
        })
        .map((s) => {
          return {
            ...s.form,
            isIceDesignCertified: !!isSelectAll,
            action: 'certify',
            projectFile: undefined,
          } as IUpsertProjectTransmittalItemRevisionRequest;
        });

      mutateUpsertTransmittalItemsAndRevisions({ transmittalId, request });
    },
    [JSON.stringify(items)],
  );

  useEffect(() => {
    if (!dataUpsertTransmittalItemsAndRevisions) return;

    updateCacheFromUpdateTransmittalItemResponseByDocId(
      docType.id,
      dataUpsertTransmittalItemsAndRevisions?.items ?? [],
    );
    resetUpsertTransmittalItemsAndRevisions();
  }, [JSON.stringify(dataUpsertTransmittalItemsAndRevisions)]);

  return {
    isSelectAllTransmittalRevisionsForApproval,
    onClickSelectAll,
    mutationSelectAllTransmittalRevisionsForApproval: mutationUpsertTransmittalItemsAndRevisions,
  };
};

export const useRefetch = (isDirty = false, isBlockRefetch = false) => {
  useEffect(() => {
    focusManager.setEventListener((handleFocus) => {
      const handle = () => {
        if (isDirty || isBlockRefetch) return;
        handleFocus();
      };

      if (typeof window !== 'undefined' && window.addEventListener) {
        window.addEventListener('visibilitychange', handle, false);
        return () => {
          window.removeEventListener('visibilitychange', () => handle());
        };
      }
    });
  }, [isDirty, isBlockRefetch]);
};

export const useFocus = () => {
  const [isWindowFocus, setIsWindowFocus] = useState(true);

  useEffect(() => {
    const onFocus = () => {
      setTimeout(() => {
        setIsWindowFocus(true);
      }, 1000);
    };
    const onBlur = () => {
      setIsWindowFocus(false);
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  return isWindowFocus;
};
